import React, { useState } from 'react';
import { useTheme, Theme } from '../../../context';
import UIcon from '../../../assets/uicons/UIcon';
import styles from './ThemeToggle.module.scss';

// ==============================================
// THEME TOGGLE COMPONENT
// ==============================================

export interface ThemeToggleProps {
  /** Variante del toggle */
  variant?: 'button' | 'switch' | 'dropdown';
  /** Tamaño del componente */
  size?: 'sm' | 'md' | 'lg';
  /** Mostrar etiquetas */
  showLabels?: boolean;
  /** Mostrar opción de sistema */
  showSystemOption?: boolean;
  /** Posición del dropdown */
  dropdownPosition?: 'top' | 'bottom' | 'left' | 'right';
  /** Clase CSS adicional */
  className?: string;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  variant = 'button',
  size = 'md',
  showLabels = false,
  showSystemOption = true,
  dropdownPosition = 'bottom',
  className = ''
}) => {
  const { theme, themeMode, setTheme, toggleTheme } = useTheme();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // ==============================================
  // ICONS
  // ==============================================

  const SunIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
      <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2"/>
    </svg>
  );

  const MoonIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" fill="currentColor"/>
    </svg>
  );

  const SystemIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
      <path d="M8 8h8v8H8z" stroke="currentColor" strokeWidth="2"/>
    </svg>
  );

  const ChevronDownIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2"/>
    </svg>
  );

  // ==============================================
  // RENDER METHODS
  // ==============================================

  const getCurrentIcon = () => {
    if (themeMode === 'system') return <SystemIcon />;
    return theme === 'dark' ? <MoonIcon /> : <SunIcon />;
  };

  const getCurrentLabel = () => {
    if (themeMode === 'system') return 'Sistema';
    return theme === 'dark' ? 'Oscuro' : 'Claro';
  };

  const themeOptions: Array<{ value: Theme; label: string; icon: React.ReactNode }> = [
    { value: 'light', label: 'Claro', icon: <SunIcon /> },
    { value: 'dark', label: 'Oscuro', icon: <MoonIcon /> },
    ...(showSystemOption ? [{ value: 'system' as Theme, label: 'Sistema', icon: <SystemIcon /> }] : [])
  ];

  // ==============================================
  // BUTTON VARIANT
  // ==============================================

  if (variant === 'button') {
    return (
      <button
        onClick={toggleTheme}
        className={`${styles.toggleButton} ${styles[size]} ${className}`}
        aria-label={`Cambiar tema. Actual: ${getCurrentLabel()}`}
        title={`Cambiar tema. Actual: ${getCurrentLabel()}`}
      >
        {getCurrentIcon()}
        {showLabels && <span className={styles.label}>{getCurrentLabel()}</span>}
      </button>
    );
  }

  // ==============================================
  // SWITCH VARIANT
  // ==============================================

  if (variant === 'switch') {
    return (
      <div className={`${styles.switchContainer} ${styles[size]} ${className}`}>
        {showLabels && <UIcon name="sun" size={16} className={styles.switchLabel} />}
        <button
          onClick={toggleTheme}
          className={`${styles.switch} ${theme === 'dark' ? styles.switchDark : styles.switchLight}`}
          aria-label={`Cambiar tema. Actual: ${getCurrentLabel()}`}
          role="switch"
          aria-checked={theme === 'dark'}
        >
          <span className={styles.switchThumb} />
        </button>
        {showLabels && <UIcon name="moon" size={16} className={styles.switchLabel} />}
      </div>
    );
  }

  // ==============================================
  // DROPDOWN VARIANT
  // ==============================================

  if (variant === 'dropdown') {
    return (
      <div className={`${styles.dropdownContainer} ${styles[size]} ${className}`}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className={styles.dropdownTrigger}
          aria-label={`Seleccionar tema. Actual: ${getCurrentLabel()}`}
          aria-expanded={isDropdownOpen}
          aria-haspopup="true"
        >
          {getCurrentIcon()}
          {showLabels && <span className={styles.label}>{getCurrentLabel()}</span>}
          <ChevronDownIcon />
        </button>

        {isDropdownOpen && (
          <>
            <div 
              className={styles.dropdownOverlay}
              onClick={() => setIsDropdownOpen(false)}
            />
            <div className={`${styles.dropdownMenu} ${styles[`dropdown${dropdownPosition.charAt(0).toUpperCase() + dropdownPosition.slice(1)}`]}`}>
              {themeOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    setTheme(option.value);
                    setIsDropdownOpen(false);
                  }}
                  className={`${styles.dropdownOption} ${themeMode === option.value ? styles.dropdownOptionActive : ''}`}
                  aria-label={`Cambiar a tema ${option.label}`}
                >
                  {option.icon}
                  <span>{option.label}</span>
                  {themeMode === option.value && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return null;
};

export default ThemeToggle; 